import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { ResearchReportModal } from './ResearchReportModal';
import { toast } from 'sonner';
import {
  Zap, RefreshCw, FileText, ExternalLink, AlertTriangle,
  CheckCircle2, Search, Star, Pencil, X, Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Tiny helpers ────────────────────────────────────────────────────────────

function Label({ children, color = 'text-gray-400' }) {
  return <p className={`text-[10px] font-bold uppercase tracking-widest ${color} mb-0.5`}>{children}</p>;
}

function BigStat({ value, label, color = 'text-gray-900', sub }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <p className={`text-4xl font-black leading-none ${color}`}>{value}</p>
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Pill({ children, color }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${color}`}>
      {children}
    </span>
  );
}

function SectionTitle({ children, color = 'text-gray-800', border = 'border-gray-200' }) {
  return (
    <div className={`flex items-center gap-2 border-b-2 ${border} pb-1.5 mb-3`}>
      <p className={`text-[11px] font-black uppercase tracking-widest ${color}`}>{children}</p>
    </div>
  );
}

function KV({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-2 py-1 border-b border-gray-50 last:border-0">
      <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide w-28 shrink-0">{label}</span>
      <span className="text-sm font-semibold text-gray-800 flex-1">{value}</span>
    </div>
  );
}

const ERS_CONFIG = {
  hot:     { bg: 'bg-red-600',    text: 'text-white',       label: '🔥 HOT' },
  warm:    { bg: 'bg-orange-500', text: 'text-white',       label: '✅ WARM' },
  cold:    { bg: 'bg-blue-500',   text: 'text-white',       label: '❄️ COLD' },
  dormant: { bg: 'bg-gray-400',   text: 'text-white',       label: '💤 DORMANT' },
};

const NEWS_COLORS = {
  funding:     'bg-emerald-100 text-emerald-800',
  award:       'bg-amber-100 text-amber-800',
  partnership: 'bg-blue-100 text-blue-800',
  hiring:      'bg-violet-100 text-violet-800',
  negative:    'bg-red-100 text-red-800',
  general:     'bg-gray-100 text-gray-600',
};

function freshnessLabel(lastResearchedAt) {
  if (!lastResearchedAt) return { label: 'Never', color: 'text-gray-400' };
  const days = Math.floor((Date.now() - new Date(lastResearchedAt * 1000 || lastResearchedAt).getTime()) / 86400000);
  if (days <= 7)  return { label: `${days}d ago`, color: 'text-emerald-600' };
  if (days <= 30) return { label: `${days}d ago`, color: 'text-yellow-600' };
  if (days <= 60) return { label: `${days}d ago`, color: 'text-orange-600' };
  return { label: `${days}d ago`, color: 'text-red-600' };
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CompanyResearchTab({ companyId, companyName }) {
  const [research, setResearch]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [running, setRunning]       = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportContent, setReportContent] = useState('');
  const [techEditing, setTechEditing] = useState(false);
  const [techDraft, setTechDraft]     = useState({});
  const [techSaving, setTechSaving]   = useState(false);

  const fetchResearch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/companies/${companyId}/research`, { withCredentials: true });
      setResearch(res.data);
      if (['in_progress', 'queued'].includes(res.data?.research_status)) {
        setTimeout(fetchResearch, 4000);
      } else {
        setRunning(false);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { fetchResearch(); }, [fetchResearch]);

  const startResearch = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/api/companies/${companyId}/research`, {}, { withCredentials: true });
      setResearch(prev => ({ ...(prev || {}), research_status: 'in_progress' }));
      setTimeout(fetchResearch, 2000);
    } catch { setRunning(false); }
  };

  const openReport = async () => {
    try {
      const res = await axios.get(`${API}/api/companies/${companyId}/research/report`, { withCredentials: true });
      setReportContent(res.data.content);
    } catch { setReportContent(''); }
    setReportOpen(true);
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" />
        Loading intelligence...
      </div>
    );
  }

  const noResearch  = !research || research.status === 'not_researched';
  const isInProgress = ['in_progress', 'queued'].includes(research?.research_status);

  // ── Empty / In-Progress ──
  if (noResearch || isInProgress) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
        <Search className="w-14 h-14 text-gray-200" />
        <div>
          <p className="text-lg font-black text-gray-700">
            {isInProgress ? 'Research In Progress...' : 'No Intelligence Data Yet'}
          </p>
          <p className="text-sm text-gray-400 mt-1 max-w-sm">
            {isInProgress
              ? 'Gathering intelligence — takes 1–3 minutes. Auto-refreshing...'
              : 'Run research to pull decision makers, tech stack, hiring data, news, and more.'}
          </p>
        </div>
        {isInProgress
          ? <div className="w-48 h-1.5 bg-blue-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500 animate-pulse w-2/3 rounded-full" /></div>
          : <Button onClick={startResearch} disabled={running} className="gap-2 font-bold"><Zap className="w-4 h-4" />Run Company Research</Button>
        }
      </div>
    );
  }

  const startTechEdit = () => {
    const t = research.tech_stack || {};
    setTechDraft({
      ats_used:              t.ats_used || '',
      crm_used:              t.crm_used || '',
      hrms_used:             t.hrms_used || '',
      vms_platform:          t.vms_platform || '',
      talent_sourcing_tools: (t.talent_sourcing_tools || []).join(', '),
      full_stack:            (t.full_stack || []).join(', '),
    });
    setTechEditing(true);
  };

  const cancelTechEdit = () => { setTechEditing(false); setTechDraft({}); };

  const saveTechEdit = async () => {
    setTechSaving(true);
    try {
      const payload = {
        ats_used:              techDraft.ats_used.trim() || null,
        crm_used:              techDraft.crm_used.trim() || null,
        hrms_used:             techDraft.hrms_used.trim() || null,
        vms_platform:          techDraft.vms_platform.trim() || null,
        talent_sourcing_tools: techDraft.talent_sourcing_tools.split(',').map(s => s.trim()).filter(Boolean),
        full_stack:            techDraft.full_stack.split(',').map(s => s.trim()).filter(Boolean),
      };
      await axios.patch(`${API}/api/companies/${companyId}/research/tech-stack`, payload, { withCredentials: true });
      await fetchResearch();
      setTechEditing(false);
      toast.success('Tech stack updated');
    } catch {
      toast.error('Failed to save tech stack');
    } finally {
      setTechSaving(false);
    }
  };

  // ── Data ──
  const ov       = research.overview || {};
  const tech     = research.tech_stack || {};
  const gd       = research.reviews?.glassdoor || {};
  const hiring   = research.hiring || {};
  const rec      = research.recognition || {};
  const ta       = research.talent_acquisition || {};
  const staffing = research.staffing || {};
  const news     = research.news?.items || [];
  const triggers = research.trigger_events || [];
  const risks    = research.risk_signals || [];
  const outreach = research.outreach || {};
  const band     = research.engagement_band || 'cold';
  const score    = research.engagement_score ?? 0;
  const ers      = ERS_CONFIG[band] || ERS_CONFIG.cold;
  const fresh    = freshnessLabel(research.last_researched_at);
  const completeness = research.completeness_score || 0;

  const velocityColor =
    hiring.velocity_trend === 'growing'   ? 'text-emerald-600' :
    hiring.velocity_trend === 'shrinking' ? 'text-red-500' :
    hiring.velocity_trend === 'burst'     ? 'text-amber-600' : 'text-gray-500';

  const gdColor =
    !gd.overall_rating ? 'text-gray-400' :
    gd.overall_rating >= 4.0 ? 'text-emerald-600' :
    gd.overall_rating >= 3.0 ? 'text-amber-600' : 'text-red-500';

  return (
    <div className="pb-10 space-y-5">

      {/* ── TOP HEADER BAR ─────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 rounded-xl bg-gray-900 text-white">
        <div className="flex items-center gap-5">
          {/* ERS Score */}
          <div className="text-center">
            <p className="text-5xl font-black leading-none">{score}</p>
            <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mt-1">ERS Score</p>
          </div>
          <div className="w-px h-12 bg-gray-700" />
          {/* Band */}
          <div>
            <span className={`text-lg font-black px-3 py-1 rounded-lg ${ers.bg} ${ers.text}`}>
              {ers.label}
            </span>
            <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1.5">Engagement Band</p>
          </div>
          <div className="w-px h-12 bg-gray-700" />
          {/* Freshness */}
          <div>
            <p className={`text-xl font-black ${fresh.color}`}>{fresh.label}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">Last Researched</p>
          </div>
          <div className="w-px h-12 bg-gray-700 hidden sm:block" />
          {/* Completeness */}
          <div className="hidden sm:block">
            <p className="text-xl font-black text-white">{completeness}%</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">Data Complete</p>
          </div>
        </div>
        <div className="flex gap-2">
          {research.has_report && (
            <Button variant="outline" size="sm" onClick={openReport}
              className="gap-1.5 border-gray-600 text-gray-200 hover:bg-gray-700 hover:text-white bg-transparent font-bold">
              <FileText className="w-3.5 h-3.5" /> Report
            </Button>
          )}
          <Button size="sm" onClick={startResearch} disabled={running}
            className="gap-1.5 bg-blue-500 hover:bg-blue-400 text-white font-bold">
            <RefreshCw className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Running...' : 'Re-run'}
          </Button>
        </div>
      </div>

      {/* ── RISK SIGNALS (only if present) ─────────────── */}
      {risks.length > 0 && (
        <div className="rounded-xl border-2 border-red-200 bg-red-50 px-5 py-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-2">⚠ Risk Signals</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {risks.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${r.severity === 'high' ? 'text-red-600' : 'text-orange-500'}`} />
                <p className="text-sm font-semibold text-red-900">{r.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── WHY CALL NOW ────────────────────────────────── */}
      {triggers.length > 0 && (
        <div className="rounded-xl border-2 border-amber-200 bg-amber-50 px-5 py-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-3">⚡ Why Call Now</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {triggers.map((t, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm font-semibold text-amber-900">{t}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── BIG STATS STRIP ─────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-px bg-gray-200 rounded-xl overflow-hidden border border-gray-200">
        {[
          {
            value: ov.employee_count_linkedin ? ov.employee_count_linkedin.toLocaleString() : ov.employee_count,
            label: 'Employees',
            color: 'text-blue-700'
          },
          {
            value: hiring.current_open_jobs !== undefined ? hiring.current_open_jobs : null,
            label: 'Open Jobs',
            color: hiring.current_open_jobs > 0 ? 'text-violet-700' : 'text-gray-400',
            sub: hiring.velocity_trend ? `${hiring.velocity_trend}` : null
          },
          {
            value: gd.overall_rating ? `${gd.overall_rating}/5` : null,
            label: 'Glassdoor',
            color: gdColor,
            sub: gd.review_count ? `${gd.review_count.toLocaleString()} reviews` : null
          },
          {
            value: ov.founded || null,
            label: 'Founded',
            color: 'text-gray-800'
          },
          {
            value: ov.location_count > 0 ? ov.location_count : null,
            label: 'Locations',
            color: 'text-gray-800'
          },
        ].filter(s => s.value !== null && s.value !== undefined && s.value !== '').map((s, i) => (
          <div key={i} className="bg-white px-4 py-4 flex flex-col justify-center">
            <BigStat value={s.value} label={s.label} color={s.color} sub={s.sub} />
          </div>
        ))}
      </div>

      {/* ── MAIN 3-COL GRID ─────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Company Overview */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <SectionTitle color="text-blue-700" border="border-blue-200">Company Overview</SectionTitle>
          <KV label="Industry"   value={ov.industry} />
          <KV label="HQ"         value={ov.headquarters} />
          <KV label="Revenue"    value={ov.revenue_estimate} />
          <KV label="Type"       value={ov.company_type} />
          <KV label="Ticker"     value={ov.stock_ticker} />
          {ov.linkedin_url && (
            <a href={ov.linkedin_url} target="_blank" rel="noopener noreferrer"
               className="mt-3 flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline">
              <ExternalLink className="w-3 h-3" /> LinkedIn Profile
            </a>
          )}
          {ov.description && (
            <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100 leading-relaxed line-clamp-5">
              {ov.description}
            </p>
          )}
        </div>

        {/* Tech Stack */}
        <div className={`bg-white rounded-xl border p-5 ${techEditing ? 'border-violet-400 ring-2 ring-violet-100' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className={`flex items-center gap-2 border-b-2 border-violet-200 pb-1.5 flex-1`}>
              <p className="text-[11px] font-black uppercase tracking-widest text-violet-700">Tech Stack</p>
            </div>
            {!techEditing ? (
              <button onClick={startTechEdit} className="ml-3 p-1 rounded hover:bg-violet-50 text-gray-400 hover:text-violet-600 transition-colors" title="Edit tech stack">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            ) : (
              <div className="ml-3 flex gap-1">
                <button onClick={saveTechEdit} disabled={techSaving} className="p-1 rounded bg-violet-600 hover:bg-violet-700 text-white transition-colors" title="Save">
                  {techSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                </button>
                <button onClick={cancelTechEdit} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Cancel">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {techEditing ? (
            /* ── Edit mode ── */
            <div className="space-y-3">
              {[
                { key: 'ats_used',     label: 'ATS',          placeholder: 'e.g. Greenhouse' },
                { key: 'crm_used',     label: 'CRM',          placeholder: 'e.g. Salesforce' },
                { key: 'hrms_used',    label: 'HRMS',         placeholder: 'e.g. Workday' },
                { key: 'vms_platform', label: 'VMS / MSP',    placeholder: 'e.g. Fieldglass' },
              ].map(({ key, label, placeholder }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide w-20 shrink-0">{label}</span>
                  <input
                    type="text"
                    value={techDraft[key] || ''}
                    onChange={e => setTechDraft(d => ({ ...d, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="flex-1 text-sm font-semibold border border-gray-200 rounded-md px-2.5 py-1 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                  />
                </div>
              ))}
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Sourcing Tools <span className="normal-case font-normal">(comma-separated)</span></p>
                <input
                  type="text"
                  value={techDraft.talent_sourcing_tools || ''}
                  onChange={e => setTechDraft(d => ({ ...d, talent_sourcing_tools: e.target.value }))}
                  placeholder="LinkedIn Recruiter, SeekOut, HireEZ"
                  className="w-full text-sm font-semibold border border-gray-200 rounded-md px-2.5 py-1 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200"
                />
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Full Stack <span className="normal-case font-normal">(comma-separated)</span></p>
                <textarea
                  value={techDraft.full_stack || ''}
                  onChange={e => setTechDraft(d => ({ ...d, full_stack: e.target.value }))}
                  placeholder="React, Node.js, AWS, PostgreSQL"
                  rows={3}
                  className="w-full text-sm font-semibold border border-gray-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200 resize-none"
                />
              </div>
            </div>
          ) : (
            /* ── View mode ── */
            <>
              {[
                { label: 'ATS',      value: tech.ats_used,     bg: 'bg-violet-50 text-violet-800' },
                { label: 'CRM',      value: tech.crm_used,     bg: 'bg-blue-50 text-blue-800' },
                { label: 'HRMS',     value: tech.hrms_used,    bg: 'bg-emerald-50 text-emerald-800' },
                { label: 'VMS/MSP',  value: tech.vms_platform, bg: 'bg-amber-50 text-amber-800' },
              ].map(({ label, value, bg }) => (
                <div key={label} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{label}</span>
                  <span className={`text-xs font-black px-2 py-0.5 rounded-md ${value ? bg : 'text-gray-300'}`}>
                    {value || '—'}
                  </span>
                </div>
              ))}
              {tech.talent_sourcing_tools?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Sourcing Tools</p>
                  <div className="flex flex-wrap gap-1">
                    {tech.talent_sourcing_tools.map((t, i) => (
                      <span key={i} className="text-xs bg-purple-50 text-purple-700 font-semibold px-2 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {tech.full_stack?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Full Stack</p>
                  <div className="flex flex-wrap gap-1">
                    {tech.full_stack.slice(0, 18).map((t, i) => (
                      <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* TA Team */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <SectionTitle color="text-emerald-700" border="border-emerald-200">Talent Acquisition</SectionTitle>
          {ta.top_ta_leader?.name ? (
            <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <p className="text-sm font-black text-gray-900">{ta.top_ta_leader.name}</p>
              <p className="text-xs font-semibold text-emerald-700 mt-0.5">{ta.top_ta_leader.title}</p>
              {ta.top_ta_leader.linkedin_url && (
                <a href={ta.top_ta_leader.linkedin_url} target="_blank" rel="noopener noreferrer"
                   className="mt-1.5 flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline">
                  <ExternalLink className="w-3 h-3" /> LinkedIn
                </a>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 mb-3">No TA leader identified</p>
          )}
          <KV label="Recruiters" value={ta.in_house_recruiter_count || null} />
          <KV label="TA Structure" value={ta.ta_org_structure || null} />
          {ta.ta_recent_changes?.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Recent Changes</p>
              {ta.ta_recent_changes.map((c, i) => (
                <p key={i} className="text-xs font-semibold text-gray-700 py-0.5">• {c}</p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── HIRING + GLASSDOOR + STAFFING ───────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Hiring Velocity */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <SectionTitle color="text-violet-700" border="border-violet-200">Hiring Velocity</SectionTitle>
          <div className="flex items-end gap-6 mb-4">
            <div>
              <p className="text-5xl font-black text-gray-900 leading-none">{hiring.current_open_jobs ?? '—'}</p>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Open Now</p>
            </div>
            {hiring.jobs_90d_ago !== undefined && (
              <div>
                <p className="text-3xl font-black text-gray-400 leading-none">{hiring.jobs_90d_ago}</p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">90d Ago</p>
              </div>
            )}
            {hiring.velocity_trend && (
              <div>
                <p className={`text-xl font-black uppercase ${velocityColor}`}>
                  {hiring.velocity_trend === 'growing' ? '↑' : hiring.velocity_trend === 'shrinking' ? '↓' : '→'} {hiring.velocity_trend}
                </p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Trend</p>
              </div>
            )}
          </div>
          {hiring.top_hiring_departments?.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Top Departments</p>
              <div className="flex flex-wrap gap-1">
                {hiring.top_hiring_departments.map((d, i) => (
                  <span key={i} className="text-xs font-bold bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full">{d}</span>
                ))}
              </div>
            </div>
          )}
          {hiring.open_roles_sample?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Sample Roles</p>
              {hiring.open_roles_sample.slice(0, 6).map((r, i) => (
                <p key={i} className="text-xs font-semibold text-gray-600 py-0.5 border-b border-gray-50 last:border-0">• {r}</p>
              ))}
            </div>
          )}
        </div>

        {/* Glassdoor */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <SectionTitle color="text-amber-700" border="border-amber-200">Glassdoor</SectionTitle>
          {gd.overall_rating ? (
            <>
              <div className="flex items-center gap-4 mb-4">
                <p className={`text-6xl font-black leading-none ${gdColor}`}>{gd.overall_rating}</p>
                <div>
                  <div className="flex gap-0.5 mb-1">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className={`w-4 h-4 ${n <= Math.round(gd.overall_rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} />
                    ))}
                  </div>
                  <p className="text-xs font-bold text-gray-500">{gd.review_count?.toLocaleString()} reviews</p>
                </div>
              </div>
              <KV label="CEO Approval"  value={gd.ceo_approval} />
              <KV label="Recommend"     value={gd.recommend_to_friend} />
              {gd.pros_summary && (
                <div className="mt-2">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide mb-0.5">Pros</p>
                  <p className="text-xs text-gray-600 font-medium leading-relaxed">{gd.pros_summary}</p>
                </div>
              )}
              {gd.cons_summary && (
                <div className="mt-2">
                  <p className="text-[10px] font-bold text-red-500 uppercase tracking-wide mb-0.5">Cons</p>
                  <p className="text-xs text-gray-600 font-medium leading-relaxed">{gd.cons_summary}</p>
                </div>
              )}
              {gd.url && (
                <a href={gd.url} target="_blank" rel="noopener noreferrer"
                   className="mt-3 flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline">
                  <ExternalLink className="w-3 h-3" /> View on Glassdoor
                </a>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400 font-semibold">No Glassdoor data</p>
          )}
        </div>

        {/* Staffing Partners + Recognition */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <SectionTitle color="text-rose-700" border="border-rose-200">Staffing Partners</SectionTitle>
            <KV label="Model"    value={staffing.vendor_list_type} />
            <KV label="VMS/MSP"  value={staffing.msp_vms_platform} />
            {staffing.current_staffing_partners?.length > 0 ? (
              <div className="mt-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Known Partners</p>
                <div className="flex flex-wrap gap-1">
                  {staffing.current_staffing_partners.map((p, i) => (
                    <span key={i} className="text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-full">{p}</span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400 mt-2 font-medium">No partners detected — opportunity to discover</p>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <SectionTitle color="text-amber-700" border="border-amber-200">Recognition</SectionTitle>
            {!rec.is_inc5000 && !rec.is_sia_listed && !rec.is_deloitte_fast500 && (!rec.awards || rec.awards.length === 0) ? (
              <p className="text-xs text-gray-400 font-medium">No major listings detected</p>
            ) : (
              <div className="space-y-1.5">
                {rec.is_inc5000 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-black text-amber-800">🏆 Inc. 5000</span>
                    {rec.inc5000_rank && <span className="text-xs font-bold text-amber-600">#{rec.inc5000_rank}</span>}
                  </div>
                )}
                {rec.is_sia_listed && (
                  <p className="text-sm font-black text-blue-800">📋 SIA Listed</p>
                )}
                {rec.is_deloitte_fast500 && (
                  <p className="text-sm font-black text-emerald-800">🚀 Deloitte Fast 500</p>
                )}
                {rec.awards?.map((a, i) => (
                  <p key={i} className="text-xs font-semibold text-gray-700">• {a}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── RECENT NEWS ─────────────────────────────────── */}
      {news.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <SectionTitle color="text-gray-700" border="border-gray-200">Recent News</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
            {news.map((item, i) => (
              <div key={i} className="flex items-start gap-2.5 py-2 border-b border-gray-50 last:border-0">
                <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${NEWS_COLORS[item.category] || NEWS_COLORS.general}`}>
                  {item.category || 'news'}
                </span>
                <div className="flex-1 min-w-0">
                  <a href={item.url} target="_blank" rel="noopener noreferrer"
                     className="text-sm font-bold text-gray-800 hover:text-blue-600 hover:underline line-clamp-2 leading-snug">
                    {item.title}
                  </a>
                  {item.summary && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1 font-medium">{item.summary}</p>}
                </div>
                <span className="text-[10px] font-bold text-gray-400 shrink-0 mt-0.5 whitespace-nowrap">{item.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── OUTREACH DRAFTS ─────────────────────────────── */}
      {(outreach.ta_version || outreach.csuite_version) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {outreach.ta_version && (
            <div className="bg-white rounded-xl border border-blue-200 p-5">
              <SectionTitle color="text-blue-700" border="border-blue-200">Outreach — TA Leader</SectionTitle>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-medium leading-relaxed">{outreach.ta_version}</pre>
            </div>
          )}
          {outreach.csuite_version && (
            <div className="bg-white rounded-xl border border-violet-200 p-5">
              <SectionTitle color="text-violet-700" border="border-violet-200">Outreach — C-Suite</SectionTitle>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-medium leading-relaxed">{outreach.csuite_version}</pre>
            </div>
          )}
        </div>
      )}

      {/* ── FOOTER META ─────────────────────────────────── */}
      <div className="flex flex-wrap gap-4 justify-end text-[10px] font-bold text-gray-400 uppercase tracking-widest">
        {research.research_duration_seconds > 0 && <span>⏱ {research.research_duration_seconds}s runtime</span>}
        {research.research_cost_apify_credits > 0 && <span>💳 ${research.research_cost_apify_credits.toFixed(3)} apify</span>}
        <span>📊 {completeness}% complete</span>
      </div>

      <ResearchReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        content={reportContent}
        companyName={companyName}
      />
    </div>
  );
}
